/**
 * Turning a time into the timecode of the frame it names.
 *
 * There were two of these, and they disagreed with each other and with the
 * rest of the app. The editor's read every field off the float separately,
 * taking the frame from `Math.floor((t % 1) * frameRate)`, so accumulated
 * error landed in the smallest unit: stepping the playhead with the arrow keys
 * adds 1/frameRate without snapping, and six presses was enough for the
 * reported frame to fall behind by one. The motion editor's collapsed the
 * frame rate to a whole number before counting with it, so an hour of 29.97
 * footage read 01:00:00:00 rather than 00:59:56:12, and it had no guard at
 * all, rendering "NaN:NaN:NaN:NaN" for a duration that had not resolved yet.
 *
 * Both now come here.
 */

/**
 * Rounding, not flooring, because a timecode names a frame rather than an
 * instant, and this app resolves a time to a frame by rounding everywhere it
 * matters: the render bridge and the frame cache both seek to
 * `Math.round(time * frameRate) / frameRate`, a clip drag commits to the
 * nearest frame, and `secondsToFrames` rounds when writing an edit out to
 * Premiere or Final Cut. A flooring label would name a frame next to the one
 * the canvas is showing and next to the one the export will contain.
 */
export function timeToFrameIndex(seconds: number, frameRate: number): number {
  if (!Number.isFinite(seconds) || seconds < 0) return 0;
  const rate = Number.isFinite(frameRate) && frameRate > 0 ? frameRate : 30;
  return Math.max(0, Math.round(seconds * rate));
}

/**
 * The whole number of frames a timecode groups into one second.
 *
 * Deliberately separate from the rate used to count them. 29.97fps footage
 * runs 29.97 frames every real second and displays them in groups of 30, so
 * the timecode falls a tenth of a percent behind the clock. That is what
 * non-drop-frame timecode is, it is the format the interchange exporters
 * already declare, and it is why the frame field can never reach the rate and
 * roll a second over twice.
 */
export function timecodeDisplayBase(frameRate: number): number {
  const rate = Number.isFinite(frameRate) && frameRate > 0 ? frameRate : 30;
  return Math.max(1, Math.round(rate));
}

const pad = (value: number): string => value.toString().padStart(2, "0");

/**
 * `HH:MM:SS:FF` for a time in seconds.
 *
 * A time that is not a real, non-negative number reads as zero rather than
 * propagating into the string. A WebM from MediaRecorder reports an infinite
 * duration until it has been seeked, and that reaches clip labels.
 */
export function formatTimecode(seconds: number, frameRate = 30): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00:00:00";

  const base = timecodeDisplayBase(frameRate);
  const totalFrames = timeToFrameIndex(seconds, frameRate);
  const frames = totalFrames % base;
  const totalSeconds = Math.floor(totalFrames / base);

  return [
    pad(Math.floor(totalSeconds / 3600)),
    pad(Math.floor(totalSeconds / 60) % 60),
    pad(totalSeconds % 60),
    pad(frames),
  ].join(":");
}

/**
 * A millionth of a frame.
 *
 * Wide enough to swallow float representation error at any timeline length the
 * editor supports, and far narrower than a real frame, so it can only pull a
 * product back onto a boundary it was already on.
 */
const FRAME_EPSILON = 1e-6;

/**
 * How many frames it takes to cover a span of time.
 *
 * Ceiling, because a span that ends part way through a frame still needs that
 * frame rendered. But a product that misses a whole number by a millionth is
 * not a partial frame, it is float error, and ceiling it added a frame the
 * timeline does not contain: 8.3 * 30 is 249.00000000000003, so an 8.3 second
 * export declared 250 frames and the render loop drew one past the end.
 */
export function framesToCover(seconds: number, frameRate: number): number {
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  const rate = Number.isFinite(frameRate) && frameRate > 0 ? frameRate : 30;
  const exact = seconds * rate;
  const nearest = Math.round(exact);
  return Math.abs(exact - nearest) < FRAME_EPSILON ? nearest : Math.ceil(exact);
}

/**
 * Which frame of a source a time lands in.
 *
 * Flooring, unlike timeToFrameIndex: this indexes into an array of decoded or
 * baked frames, where frame N covers the span from N/fps up to (N+1)/fps, so a
 * time inside that span belongs to N. The epsilon does the same job as above,
 * stopping a time that is exactly on frame N from resolving to N - 1 and
 * serving the previous frame.
 */
export function sourceFrameIndex(seconds: number, frameRate: number): number {
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  const rate = Number.isFinite(frameRate) && frameRate > 0 ? frameRate : 30;
  return Math.max(0, Math.floor(seconds * rate + FRAME_EPSILON));
}
